import React from 'react';
import { useNavigate } from 'react-router-dom';
import { COMPANY_ROUTES } from '../constants';

interface JobPostingMethodModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectId?: string;
}

const JobPostingMethodModal: React.FC<JobPostingMethodModalProps> = ({ isOpen, onClose, projectId }) => {
    const navigate = useNavigate();

    if (!isOpen) return null;

    const handleMethodSelect = (method: 'pdf' | 'form') => {
        onClose();
        if (method === 'pdf') {
            navigate('/companies/talk-to-david', {
                state: {
                    projectId,
                    fromJobPosting: true
                }
            });
        } else {
            navigate(COMPANY_ROUTES.JOB_POSTING, {
                state: { projectId }
            });
        }
    };

    return (
        <div
            className="fixed inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl w-full max-w-[720px] mx-4 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-8 pt-8 pb-2">
                    <div>
                        <h2 className="text-xl font-semibold text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            How would you like to post your job?
                        </h2>
                        <p className="text-sm text-[#757575] mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Choose the method that works best for you
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F4F4] transition-colors"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15M5 5L15 15" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                {/* Method Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-8 py-6">
                    {/* PDF Upload */}
                    <button
                        onClick={() => handleMethodSelect('pdf')}
                        className="bg-white rounded-xl p-6 border-2 border-[#EDEDED] hover:border-[#015FA6] hover:shadow-md transition-all text-left"
                    >
                        <div className="flex items-center justify-center w-12 h-12 bg-[#F0F7FF] rounded-lg mb-4">
                            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                                <path d="M20 4H8C6.9 4 6 4.9 6 6V26C6 27.1 6.9 28 8 28H24C25.1 28 26 27.1 26 26V10L20 4ZM8 26V6H19V11H24V26H8Z" fill="#015FA6" />
                                <path d="M12 18H20V20H12V18ZM12 22H20V24H12V22ZM12 14H16V16H12V14Z" fill="#015FA6" />
                            </svg>
                        </div>
                        <h3 className="text-base font-semibold text-[#0B111E] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Upload Job Description
                        </h3>
                        <p className="text-sm text-[#757575] mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Upload a PDF and let David help you refine it into a complete job posting.
                        </p>
                        <ul className="space-y-1.5 text-xs text-[#757575]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <li className="flex items-center gap-1.5">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <path d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z" fill="#015FA6" />
                                </svg>
                                Fast and convenient
                            </li>
                            <li className="flex items-center gap-1.5">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <path d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z" fill="#015FA6" />
                                </svg>
                                AI-assisted refinement
                            </li>
                            <li className="flex items-center gap-1.5">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <path d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z" fill="#015FA6" />
                                </svg>
                                Supports PDF, DOCX, TXT
                            </li>
                        </ul>
                    </button>

                    {/* Fill Form */}
                    <button
                        onClick={() => handleMethodSelect('form')}
                        className="bg-white rounded-xl p-6 border-2 border-[#EDEDED] hover:border-[#015FA6] hover:shadow-md transition-all text-left"
                    >
                        <div className="flex items-center justify-center w-12 h-12 bg-[#F0F7FF] rounded-lg mb-4">
                            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                                <path d="M25.3333 5.33333H6.66667C5.2 5.33333 4 6.53333 4 8V24C4 25.4667 5.2 26.6667 6.66667 26.6667H25.3333C26.8 26.6667 28 25.4667 28 24V8C28 6.53333 26.8 5.33333 25.3333 5.33333ZM25.3333 24H6.66667V8H25.3333V24Z" fill="#015FA6" />
                                <path d="M9.33333 12H22.6667V14.6667H9.33333V12ZM9.33333 17.3333H22.6667V20H9.33333V17.3333Z" fill="#015FA6" />
                            </svg>
                        </div>
                        <h3 className="text-base font-semibold text-[#0B111E] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Fill Out Form
                        </h3>
                        <p className="text-sm text-[#757575] mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Start from scratch with a guided form covering all the job details.
                        </p>
                        <ul className="space-y-1.5 text-xs text-[#757575]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <li className="flex items-center gap-1.5">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <path d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z" fill="#015FA6" />
                                </svg>
                                Complete control
                            </li>
                            <li className="flex items-center gap-1.5">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <path d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z" fill="#015FA6" />
                                </svg>
                                Guided step-by-step
                            </li>
                            <li className="flex items-center gap-1.5">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <path d="M6.66667 10.1147L12.7947 3.98599L13.7381 4.92866L6.66667 12L2.42267 7.75599L3.36534 6.81333L6.66667 10.1147Z" fill="#015FA6" />
                                </svg>
                                No existing job description needed
                            </li>
                        </ul>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default JobPostingMethodModal;
